"use client";

import { type FC, useState, useEffect, useMemo, useCallback } from "react";
import { SwapButton } from "./components";
import { createModalHelpers } from "@stores/unified-modal.store";
import { useSwap } from "@stores/swap.store";
import { usePools } from "@stores/pools.store";
import { CHAIN, useTonConnectUI } from "@tonconnect/ui-react";
import CurrencyForm from "./components/CurrencyForm";
import React from "react"; // For React.memo
import Flip from "./components/FlipButton/Flip";
import CardHeader from "@components/shared/card/CardHeader";
import Pools from "./components/Pools";
import { showToast, showPromiseToast } from "@utils/showToast";
import { useWallet } from "@stores/wallet.store";
import { toNanoTon } from "../../lib/utils";
import type {
    ISwapEmulationResponse,
    ISwapExternalMessagesResponsePayload,
    ITransactionResponse,
} from "@api/IWalletController";
import { initialAssets } from "@stores/assets.store"; // For TON guid
import SwapDetails from "@pages/Swap/components/SwapDetails";
import Card from "@components/shared/card/Card";
import Separator from "@/components/ui/separator";
import Currencies from "@components/shared/modals/Currencies";
import { useShallow } from "zustand/react/shallow";

const minReceive = (amount: number, slippage: number): string => {
    if (!slippage) return amount.toString();
    const result = amount - (amount * slippage) / 100;
    return result.toFixed(8);
};

const MemoizedCurrencyForm = React.memo(CurrencyForm);
const MemoizedSwapDetails = React.memo(SwapDetails);

// Find the TON asset's guid robustly by symbol
const TON_GUID = initialAssets.find((a) => a.symbol === "TON")?.guid;
const isTonAsset = (asset: any) => asset?.guid === TON_GUID;

const SwapForm: FC = () => {
    // Use Zustand selectors for each field
    const {
        sellAssetInfo,
        buyAssetInfo,
        slippagePercentage,
        setSwapAmount,
        flipAssets,
        canSwap,
        preSwapHandler,
        executeSwap,
        swapIsLoading,
        selectedPool,
        setSellAsset,
        setBuyAsset,
    } = useSwap(
        useShallow((s) => ({
            sellAssetInfo: s.sellAssetInfo,
            buyAssetInfo: s.buyAssetInfo,
            slippagePercentage: s.slippagePercentage,
            setSwapAmount: s.setSwapAmount,
            flipAssets: s.flipAssets,
            canSwap: s.canSwap,
            preSwapHandler: s.preSwapHandler,
            executeSwap: s.executeSwap,
            swapIsLoading: s.isLoading,
            selectedPool: s.selectedPool,
            setSellAsset: s.setSellAsset,
            setBuyAsset: s.setBuyAsset,
        })),
    );

    const poolsData = usePools(
        useShallow((s) => ({
            emulationResults: s.emulationResults,
            pools: s.pools,
        })),
    );

    const { emulationResults, pools } = poolsData;

    const isLoading = swapIsLoading;

    const [currentCurrencyVariant, setCurrentCurrencyVariant] = useState<"Sell" | "Buy">("Sell");
    const modalHelpers = createModalHelpers();
    const { openModal, toggleModal } = modalHelpers;
    const [isFastExchange, setIsFastExchange] = useState(false);

    const tonConnectData = useTonConnectUI();
    const [tonConnectUI] = tonConnectData;

    const walletData = useWallet(
        useShallow((state) => ({
            isConnected: state.isConnected,
            currentWallet: state.currentWallet,
        })),
    );

    const { isConnected, currentWallet } = walletData;

    const displayBuyAmount = useMemo(() => {
        if (
            !emulationResults ||
            emulationResults.length === 0 ||
            !sellAssetInfo.amount ||
            Number.parseFloat(sellAssetInfo.amount) <= 0
        ) {
            return "";
        }
        const result = selectedPool
            ? emulationResults.find((r: ISwapEmulationResponse) => r.dex === selectedPool)
            : emulationResults[0];
        return result?.amount?.toString() ?? "";
    }, [emulationResults, selectedPool, sellAssetInfo.amount]);

    const minReceiveValue = useMemo(() => {
        return minReceive(Number.parseFloat(displayBuyAmount || "0"), slippagePercentage);
    }, [displayBuyAmount, slippagePercentage, minReceive]);

    useEffect(() => {
        if (
            sellAssetInfo.asset &&
            buyAssetInfo.asset &&
            sellAssetInfo.amount &&
            Number.parseFloat(sellAssetInfo.amount) > 0
        ) {
            // Always fetch via DEX pair info if TON is involved
            if (isTonAsset(sellAssetInfo.asset) || isTonAsset(buyAssetInfo.asset)) {
                // Call DEX pair info fetch logic (already handled in preSwapHandler or pair info hooks)
                preSwapHandler();
            } else {
                preSwapHandler();
            }
        }
    }, [currentWallet, sellAssetInfo.asset, buyAssetInfo.asset, sellAssetInfo.amount, preSwapHandler]);

    const resetSwapForm = useCallback(() => {
        setSwapAmount("");
        // Optionally reset selected assets:
        // setSellAsset(null);
        // setBuyAsset(null);
        // Optionally reset other UI state if needed
    }, [setSwapAmount /*, setSellAsset, setBuyAsset*/]);

    const swapHandler = async () => {
        if (!currentWallet.isMnemonics && !tonConnectUI.connected) {
            showToast("Reconnect wallet to execute swap", "info");
            setTimeout(() => {
                tonConnectUI.openModal();
            }, 1000);
            return;
        }
        if (!isConnected) {
            showToast("Please connect your wallet first", "info");
            return;
        }

        if (!currentWallet.isMnemonics) {
            const result = await executeSwap();
            if (!result) {
                return;
            }

            const externalMessages = result as ISwapExternalMessagesResponsePayload[];
            await showPromiseToast(
                tonConnectUI.sendTransaction({
                    network: CHAIN.MAINNET,
                    validUntil: Date.now() + 1000 * 60 * 10,
                    messages: externalMessages.map(({ destinationAddress, amount, payload }) => ({
                        address: destinationAddress,
                        amount: toNanoTon(amount).toString(),
                        payload,
                    })),
                }),
                "Transaction sent",
            );
            // Reset form after successful swap
            resetSwapForm();
        } else {
            const sendIntTrx = async (
                executeSwap: () => Promise<ISwapExternalMessagesResponsePayload[] | ITransactionResponse | null>,
            ) => {
                const result = (await executeSwap()) as ITransactionResponse;
                if (!result) {
                    throw new Error("Transaction failed");
                }
                return result;
            };
            await showPromiseToast(sendIntTrx(executeSwap), "Transaction sent");
            // Reset form after successful swap
            resetSwapForm();
        }
    };

    const handleSetSellCurrency = useCallback(() => {
        setCurrentCurrencyVariant("Sell");
        openModal("currencies");
    }, [openModal]);

    const handleSetBuyCurrency = useCallback(() => {
        setCurrentCurrencyVariant("Buy");
        openModal("currencies");
    }, [openModal]);

    const { updateAssetBalances } = useSwap();
    const handleFlip = useCallback(() => {
        const calculatedBuyAmountBeforeFlip = displayBuyAmount;
        setCurrentCurrencyVariant("Sell");
        flipAssets();
        setSwapAmount(calculatedBuyAmountBeforeFlip, true);
        updateAssetBalances(); // ensure wallet tokens are refreshed
    }, [displayBuyAmount, flipAssets, setSwapAmount, updateAssetBalances]);

    return (
        <>
            <Currencies
                setAsset={currentCurrencyVariant === "Sell" ? setSellAsset : setBuyAsset}
                variant={currentCurrencyVariant}
            />
            <Card>
                <CardHeader handleReload={preSwapHandler} handleSettingsModal={() => openModal("Settings")} />
                <Separator />

                {sellAssetInfo.asset && (
                    <MemoizedCurrencyForm
                        title="Sell"
                        asset={sellAssetInfo.asset}
                        amount={sellAssetInfo.amount}
                        setAmount={setSwapAmount}
                        balance={
                            isConnected && typeof sellAssetInfo.balance === "number"
                                ? sellAssetInfo.balance.toFixed(sellAssetInfo.balance < 0.0001 ? 8 : 4)
                                : "connect wallet"
                        }
                        handleSetCurrency={handleSetSellCurrency}
                        price={sellAssetInfo.price ?? undefined}
                        percentButtons={true}
                        className="px-3"
                    />
                )}
                <Flip handleFlip={handleFlip} />

                {buyAssetInfo.asset && (
                    <MemoizedCurrencyForm
                        title="Buy"
                        asset={buyAssetInfo.asset}
                        amount={displayBuyAmount}
                        handleSetCurrency={handleSetBuyCurrency}
                        price={buyAssetInfo.price ?? undefined}
                        className="px-3"
                    />
                )}

                <Pools />
                <Separator />

                {sellAssetInfo.amount && Number.parseFloat(sellAssetInfo.amount) > 0 && pools && pools.length > 0 && (
                    <MemoizedSwapDetails
                        sellAssetInfo={sellAssetInfo}
                        buyAssetInfo={buyAssetInfo}
                        slippagePercentage={slippagePercentage}
                        minReceiveValue={minReceiveValue}
                    />
                )}

                <SwapButton
                    handleSwap={swapHandler}
                    disabled={!canSwap() || isLoading}
                    isFastExchange={isFastExchange}
                    isPending={isLoading}
                    isMnemonic={currentWallet?.isMnemonics}
                />
            </Card>
        </>
    );
};

export default SwapForm;
